import type { PropsWithChildren } from "react";
import { createContext, useContext, useMemo } from "react";
import type {
	FacetCounts,
	FilterSetter,
	FiltersConfig,
	FiltersState,
} from "./types";

// Define the shape of the context
interface FiltersContextType {
	filters: FiltersState;
	config: FiltersConfig;
	disableTopProducersFilter: boolean;
	onFilterChange: FilterSetter;
	onClearAll?: () => void;
	facets?: FacetCounts;
	onTopProducerWarning?: () => void;
}

// Create the context
const FiltersContext = createContext<FiltersContextType | undefined>(undefined);

// Define props for the provider
interface FiltersProviderProps {
	filters: FiltersState;
	config: FiltersConfig;
	disableTopProducersFilter: boolean;
	onFilterChange: FilterSetter;
	onClearAll?: () => void;
	facets?: FacetCounts;
	onTopProducerWarning?: () => void;
}

// Create the Provider component
export function FiltersProvider({
	children,
	filters,
	config,
	disableTopProducersFilter,
	onFilterChange,
	onClearAll,
	facets,
	onTopProducerWarning,
}: PropsWithChildren<FiltersProviderProps>) {
	// Memoize the context value to prevent unnecessary re-renders
	const contextValue = useMemo(
		() => ({
			filters,
			config,
			disableTopProducersFilter,
			onFilterChange,
			onClearAll,
			facets,
			onTopProducerWarning,
		}),
		[
			filters,
			config,
			disableTopProducersFilter,
			onFilterChange,
			onClearAll,
			facets,
			onTopProducerWarning,
		],
	);

	return (
		<FiltersContext.Provider value={contextValue}>
			{children}
		</FiltersContext.Provider>
	);
}

// Create a custom hook for easy access
export function useFiltersContext(): FiltersContextType {
	const context = useContext(FiltersContext);

	if (context === undefined) {
		throw new Error("useFiltersContext must be used within a FiltersProvider");
	}

	return context;
}
